import { useState, useEffect } from 'react';
import { screenshotService, ScreenshotsData } from '../services/screenshotService';
import { ProjectData } from '../services/projectDataService';

/**
 * Hook to manage screenshot capture and state
 * Wraps the existing screenshotService with React state management
 */
export function useScreenshots(
  projectData: ProjectData | null,
  gamePreviewRef: React.RefObject<any>
) {
  const [screenshots, setScreenshots] = useState<ScreenshotsData>({
    all: [],
    selected: []
  });
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);

  // Load screenshots from project metadata
  useEffect(() => {
    if (projectData?.metadata?.screenshots) {
      setScreenshots(projectData.metadata.screenshots);
    }
  }, [projectData]);

  const handleCaptureScreenshot = async (
    onSuccess: (message: string) => void,
    onError: (message: string) => void
  ) => {
    const projectId = projectData?.projectId;
    if (!projectId || !gamePreviewRef.current) return;

    setIsCapturingScreenshot(true);
    try {
      // 1. Capture and upload (uses existing service)
      const url = await screenshotService.captureAndUpload(
        gamePreviewRef.current.iframeRef,
        projectId
      );

      // 2. Update local state
      const newScreenshots: ScreenshotsData = {
        all: [...screenshots.all, url],
        selected: [...screenshots.selected]
      };

      // 3. Update metadata in DB (uses existing service)
      setScreenshots(newScreenshots);
      await screenshotService.updateScreenshotsMetadata(projectId, newScreenshots);

      onSuccess('Screenshot captured successfully!');
    } catch (error) {
      console.error('Screenshot capture failed:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to capture screenshot. Please try again.';
      onError(errorMessage);
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  const handleCaptureAllScreenshots = async (
    onSuccess: (message: string) => void,
    onError: (message: string) => void
  ) => {
    const projectId = projectData?.projectId;
    if (!projectId || !gamePreviewRef.current) return;

    setIsCapturingScreenshot(true);
    try {
      const urls = await screenshotService.captureAndUploadAll(
        gamePreviewRef.current.iframeRef,
        projectId
      );

      const existingCount = screenshots.all.length;
      const newIndices = urls.map((_, i) => existingCount + i);
      // Auto-select the new screenshots, respecting the 5-screenshot limit
      const combined = [...screenshots.selected, ...newIndices];
      const autoSelected = combined.slice(-5); // keep most recent up to 5

      const newScreenshots: ScreenshotsData = {
        all: [...screenshots.all, ...urls],
        selected: autoSelected
      };

      setScreenshots(newScreenshots);
      await screenshotService.updateScreenshotsMetadata(projectId, newScreenshots);

      onSuccess(`${urls.length} screenshot${urls.length !== 1 ? 's' : ''} captured!`);
    } catch (error) {
      console.error('All-sections capture failed:', error);
      onError(error instanceof Error ? error.message : 'Failed to capture screenshots. Please try again.');
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  return {
    screenshots,
    setScreenshots,
    isCapturingScreenshot,
    handleCaptureScreenshot,
    handleCaptureAllScreenshots
  };
}

